import React, { useEffect, useState } from 'react'

import { useFreeSpinsBonusesStore } from '@stores'
import { BSModalButton } from '@components'
import { useTranslation } from '@amusnet/translations/react'
import {
    BonusSystemState,
    Bonus,
    BonusesPlayNowMessage,
    GameUIMessageType,
    BonusesPlayLaterMessage,
    BonusStates,
} from 'game-ui-types'
import { showFlexiBundles } from '@amusnet/react-ui'

interface FSMainButtonsProps {
    bonus?: Bonus
    disablePlayNowButton?: boolean
}

const FSMainButtons: React.FC<FSMainButtonsProps> = ({ bonus, disablePlayNowButton = false }: FSMainButtonsProps) => {
    const bonusSystemState = useFreeSpinsBonusesStore((state) => state.bonusSystemState)
    const setBonusSystemState = useFreeSpinsBonusesStore((state) => state.setBonusSystemState)
    const bonuses = useFreeSpinsBonusesStore((state) => state.bonuses)
    const selectFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectFlexiBonusOption)
    const selectedFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectedFlexiBonusOption)
    const setFlexiQueue = useFreeSpinsBonusesStore((state) => state.setFlexiQueue)
    const setNonFlexiBonusIds = useFreeSpinsBonusesStore((state) => state.setNonFlexiBonusIds)
    const removeBonusFromFlexiQueue = useFreeSpinsBonusesStore((state) => state.removeBonusFromFlexiQueue)

    const { t } = useTranslation()

    const [primaryButtonText, setPrimaryButtonText] = useState('')
    const [secondaryButtonText, setSecondaryButtonText] = useState('')

    const isInSingleBonusState = bonusSystemState === BonusSystemState.SINGLE_BONUS_INITIAL
    const isInMultipleBonusesState = bonusSystemState === BonusSystemState.MULTIPLE_BONUSES_INITIAL
    const canPlayLater = isInSingleBonusState ? bonus?.playLater : bonuses.every((b) => b.playLater)

    useEffect(() => {
        switch (bonusSystemState) {
            case BonusSystemState.SINGLE_BONUS_INITIAL: {
                setPrimaryButtonText(t('gameui.playNow'))
                setSecondaryButtonText(t('gameui.playLater'))
                break
            }
            case BonusSystemState.MULTIPLE_BONUSES_INITIAL: {
                setPrimaryButtonText(t('gameui.playAllNow'))
                setSecondaryButtonText(t('gameui.playAllLater'))
                break
            }
        }
    }, [bonusSystemState])

    const playNowHandler = () => {
        if (isInMultipleBonusesState) {
            const flexiBonuses = bonuses.filter(
                (b) => (b.flexiBonusValue != null || b.flexiBonusOptions != null) && b.state === BonusStates.AWARDED
            )

            if (flexiBonuses.length > 0) {
                const normalBonusIds = bonuses
                    .filter(
                        (b) =>
                            b.flexiBonusValue == null && b.flexiBonusOptions == null && b.state === BonusStates.AWARDED
                    )
                    .map((b) => b.id)

                setNonFlexiBonusIds(normalBonusIds)
                setFlexiQueue(flexiBonuses)

                return
            }
        }

        // Single bonus modal "Play Now" action or multi-play with zero flexi bonuses
        const bonusIds =
            bonuses.length > 1
                ? bonuses.filter((b) => b.state === BonusStates.AWARDED).map((b) => b.id)
                : bonuses.map((b) => b.id)

        const targetBonus = bonus ?? bonuses[0]
        const hasFlexiValue = !!targetBonus?.flexiBonusValue

        const payload = {
            bonusIds,
            ...(showFlexiBundles && hasFlexiValue && selectedFlexiBonusOption
                ? { flexiBonusOption: selectedFlexiBonusOption }
                : {}),
        }

        const message: BonusesPlayNowMessage = {
            type: GameUIMessageType.BONUSES_PLAY_NOW,
            payload,
        }

        if (isInSingleBonusState) {
            if (!bonus) {
                return
            }

            window.postMessage(message)
            setBonusSystemState(BonusSystemState.IN_PLAY)

            removeBonusFromFlexiQueue(bonus.id)

            if (showFlexiBundles) {
                selectFlexiBonusOption(null)
            }
        } else if (isInMultipleBonusesState) {
            window.postMessage(message)
            setBonusSystemState(BonusSystemState.IN_PLAY)

            if (showFlexiBundles) {
                selectFlexiBonusOption(null)
            }
        }
    }

    const playLaterHandler = () => {
        if (isInSingleBonusState && !bonus) {
            return
        }

        if (showFlexiBundles && selectedFlexiBonusOption) {
            selectFlexiBonusOption(null)
        }

        if (isInSingleBonusState || isInMultipleBonusesState) {
            const message: BonusesPlayLaterMessage = {
                type: GameUIMessageType.BONUSES_PLAY_LATER,
                payload: { bonusIds: bonuses.map((b) => b.id) },
            }

            window.postMessage(message)
            setBonusSystemState(BonusSystemState.INACTIVE)
        }
    }

    return (
        <>
            <div className="flex justify-center gap-[20px] w-full px-[77px] pt-[24px] pb-[24px]">
                {canPlayLater && (
                    <BSModalButton
                        id={secondaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                        text={secondaryButtonText}
                        isPrimary={false}
                        onClick={playLaterHandler}
                    />
                )}
                <BSModalButton
                    id={primaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                    text={primaryButtonText}
                    onClick={playNowHandler}
                    isDisabled={disablePlayNowButton}
                />
            </div>
        </>
    )
}

export default FSMainButtons
